/**
 * A Ref DCL pool's bid side over time: what a seller of the asset can be paid,
 * and how that is changing (blackbeard/observer#41).
 *
 * The ladder above says what is bid now. This says where it came from and
 * where it is going, which is the part a seller deciding *when* to sell needs:
 *
 * - **Posted** is limit-order money. It is most of the bid side and its owner
 *   can take it back at any moment, so it is shown apart from everything else.
 * - **Held otherwise** moves only by swaps or a provider withdrawing.
 * - **Net swapped in** is buyers' payments less what sellers were paid: falling
 *   means sellers are outpacing buyers.
 * - **Selling N fetches** is the pool's own `quote` view at fixed sizes: a
 *   computation from pool state, never an offer — the solver's spread and the
 *   pool's fee come on top.
 *
 * Every chart is one series in one hue (CLAUDE.md "Charts"): four tiles, each
 * its own sparkline, rather than one chart with four colours. The book then and
 * now, and every level change, are tables.
 */

import { useEffect, useState } from 'react'

import type { BidSide } from '../api/generated/BidSide'
import { RequestFailed, fetchRefBids } from '../api/rest'
import { price as fmtPrice, utc, when } from '../lib/format'
import type { SparkPoint } from './Sparkline'
import { TrendStat } from './StatBar'

/** Spans offered, in hours. */
const SPANS: [number, string][] = [
  [6, '6 h'],
  [24, '24 h'],
  [24 * 7, '7 d'],
]

const REFRESH_MS = 60_000

/** Whole units, compact. */
function amount(v: number, places = 0): string {
  return v.toLocaleString('en-US', { maximumFractionDigits: places })
}

/** How each kind of change reads. */
const CHANGE: Record<string, string> = {
  added: 'posted',
  raised: 'raised',
  filled: 'taken by a swap',
  cancelled: 'withdrawn',
}

export function BidSidePanel({
  chain,
  poolId,
  refresh,
}: {
  chain: string
  poolId: string
  /** Changes when the pools were re-read, so the panel follows them. */
  refresh: string
}) {
  const [hours, setHours] = useState(24)
  const [size, setSize] = useState(0)
  const [data, setData] = useState<BidSide | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const load = () =>
      fetchRefBids(chain, poolId, hours, controller.signal)
        .then((d) => {
          setData(d)
          setError(null)
        })
        .catch((e: unknown) => {
          if (controller.signal.aborted) return
          setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
        })
    load()
    const id = window.setInterval(load, REFRESH_MS)
    return () => {
      window.clearInterval(id)
      controller.abort()
    }
  }, [chain, poolId, hours, refresh])

  const asset = data?.asset_symbol ?? 'the asset'
  const counter = data?.counter_symbol ?? 'the counter token'
  const points = data?.points ?? []
  const last = points[points.length - 1]
  const spark = (
    pick: (p: (typeof points)[number]) => number | null,
    fmt: (v: number) => string,
  ) =>
    points.length > 1
      ? points.map<SparkPoint>((p) => {
          const v = pick(p)
          return { value: v, label: v === null ? `— · ${when(p.at)}` : `${fmt(v)} · ${when(p.at)}` }
        })
      : null
  const sizes = data?.quote_sizes ?? []
  const quoteAt = (p: (typeof points)[number] | undefined) => p?.quotes[size] ?? null

  // The book now against then, one row per price either side has.
  const levels = new Map<number, { now: number; then: number; absorbs: number }>()
  for (const l of data?.book_then ?? [])
    levels.set(l.price, { now: 0, then: l.counter, absorbs: 0 })
  for (const l of data?.book_now ?? []) {
    const row = levels.get(l.price) ?? { now: 0, then: 0, absorbs: 0 }
    levels.set(l.price, { ...row, now: l.counter, absorbs: l.absorbs })
  }
  const depth = [...levels.entries()].sort((a, b) => b[0] - a[0])

  return (
    <section className="panel" style={{ marginBottom: 26 }}>
      <div className="panel-head">
        <h2 className="panel-title">Bid side</h2>
        <span className="eyebrow">
          what a {asset} seller can be paid
          {data?.recorded_since ? ` · recorded since ${when(data.recorded_since)}` : ''}
        </span>
      </div>
      <div className="pager" style={{ justifyContent: 'flex-start' }}>
        {SPANS.map(([h, label]) => (
          <button
            key={h}
            className="segmented panel-button"
            aria-pressed={hours === h}
            onClick={() => setHours(h)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="empty">{error}</p>}
      {!error && !data && <p className="empty">Reading the bid side…</p>}
      {data && points.length === 0 && (
        <p className="empty">
          Nothing recorded yet. The pool is read once a minute, and its history starts with the
          first read.
        </p>
      )}

      {data && last && (
        <>
          <div className="stats">
            <TrendStat
              label="Posted bids"
              value={`${amount(last.posted)} ${counter}`}
              note="limit orders — cancellable at any moment"
              points={spark(
                (p) => p.posted,
                (v) => `${amount(v)} ${counter}`,
              )}
            />
            <TrendStat
              label="Held otherwise"
              value={`${amount(last.held, 2)} ${counter}`}
              note="liquidity, buyers' payments and fees"
              points={spark(
                (p) => p.held,
                (v) => `${amount(v, 2)} ${counter}`,
              )}
            />
            <TrendStat
              label="Net swapped in"
              value={`${amount(last.net_swapped, 2)} ${counter}`}
              note="bought in less sold out, since the pool opened"
              points={spark(
                (p) => p.net_swapped,
                (v) => `${amount(v, 2)} ${counter}`,
              )}
            />
            <TrendStat
              label={
                sizes.length ? `Selling ${amount(sizes[size] ?? 0)} ${asset} fetches` : 'Quote'
              }
              value={quoteAt(last) === null ? '—' : `${fmtPrice(quoteAt(last))} ${counter} each`}
              note="the pool's own quote — not an offer"
              points={spark(
                (p) => quoteAt(p),
                (v) => `${fmtPrice(v)} ${counter} each`,
              )}
            />
          </div>
          <div className="pager" style={{ justifyContent: 'flex-start' }}>
            <span className="eyebrow">quote for selling</span>{' '}
            {sizes.map((s, i) => (
              <button
                key={s}
                className="segmented panel-button"
                aria-pressed={size === i}
                onClick={() => setSize(i)}
              >
                {amount(s)} {asset}
              </button>
            ))}
          </div>
        </>
      )}

      {data && depth.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">
              The limit-order bids now and {data.hours} hours ago
            </caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  Price of {asset}
                </th>
                <th scope="col">
                  Bid {data.book_then_at ? `at ${when(data.book_then_at)}` : 'then'}
                </th>
                <th scope="col">Bid now</th>
                <th scope="col">Change</th>
                <th scope="col">{asset} it absorbs now</th>
              </tr>
            </thead>
            <tbody>
              {depth.map(([p, l]) => (
                <tr key={p}>
                  <td className="left numeral">{fmtPrice(p)}</td>
                  <td className="numeral">{l.then ? amount(l.then) : '—'}</td>
                  <td className="numeral">{l.now ? amount(l.now) : '—'}</td>
                  <td className="numeral">
                    {l.now === l.then
                      ? '—'
                      : `${l.now > l.then ? '+' : '−'}${amount(Math.abs(l.now - l.then))}`}
                  </td>
                  <td className="numeral">{l.absorbs ? amount(l.absorbs, 1) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.changes.length > 0 && (
        <div className="scroll-x">
          <table className="board">
            <caption className="visually-hidden">Changes to the bid levels, newest first</caption>
            <thead>
              <tr>
                <th scope="col" className="left">
                  When
                </th>
                <th scope="col" className="left">
                  Price of {asset}
                </th>
                <th scope="col" className="left">
                  What happened
                </th>
                <th scope="col">Before</th>
                <th scope="col">After</th>
              </tr>
            </thead>
            <tbody>
              {data.changes.map((c, i) => (
                <tr key={`${c.at}-${c.price}-${c.kind}-${i}`}>
                  <td className="left numeral" title={utc(c.at)}>
                    {when(c.at)}
                  </td>
                  <td className="left numeral">{fmtPrice(c.price)}</td>
                  <td className="left">{CHANGE[c.kind] ?? c.kind}</td>
                  <td className="numeral">{amount(c.before)}</td>
                  <td className="numeral">{amount(c.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="panel-note">
        Read from the pool contract once a minute and recorded from the first read, so no history
        exists before it. &ldquo;Taken by a swap&rdquo; means a bid shrank by no more than the pool
        paid out in {counter} in the same minute — consistent with a seller filling it, though the
        views cannot prove which swap; a shrink beyond that is &ldquo;withdrawn&rdquo;. The quote is
        the pool&apos;s own computation from its state, not anyone&apos;s offer: a seller going
        through NEAR Intents also pays a solver&apos;s spread, and every swap pays the pool&apos;s
        fee.
      </p>
    </section>
  )
}
